import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/base/hover-card";
import { CURRENCY } from "@/data/numbers";
import { routes } from "@/data/routes";
import { useOnClickOutside } from "@/hooks/useOnClickOutside";
import { type Cart } from "@/lib/cart";
import { cn } from "@/lib/components";
import { formatPrice } from "@/lib/numbers";
import { ShoppingCartIcon } from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useRef } from "react";
import { CartItems, type CartItemsProps } from "./CartItems";

export function HeaderCartIcon(props: {
  cartItems: Cart["items"];
  onDelete: CartItemsProps["onDelete"];
  isCartPopupOpened: boolean;
  onCartPopupChange: (isOpened: boolean) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useOnClickOutside(ref, () => {
    if (props.isCartPopupOpened) {
      props.onCartPopupChange(false);
    }
  });
  const pathname = usePathname();
  if (pathname === routes.cart.path) {
    return null;
  }

  return (
    <HoverCard
      openDelay={10}
      closeDelay={100}
      onOpenChange={props.onCartPopupChange}
      open={props.cartItems.length > 0 && props.isCartPopupOpened}
    >
      <HoverCardTrigger asChild>
        <span>
          <ShoppingCartIcon />
          {props.cartItems.length > 0 && (
            <div
              className={cn(
                "absolute -right-2 -bottom-2",
                "flex items-center justify-center text-white",
                "size-4 rounded-full bg-red-400 text-[10px]",
              )}
            >
              {props.cartItems.length}
            </div>
          )}
        </span>
      </HoverCardTrigger>
      <HoverCardContent className="w-100" align="end" ref={ref}>
        <h3 className="flex gap-1 font-bold">
          Cart <p className="mb-4 font-medium text-gray-500">{`(${props.cartItems.length})`}</p>
        </h3>
        <CartItems items={props.cartItems} onDelete={props.onDelete} />

        <span className="flex justify-between py-4 font-bold">
          <p>Total</p>
          <p>{formatPrice(calculateCartTotal(props.cartItems), CURRENCY)}</p>
        </span>
        <Link
          href="/cart"
          className="block w-full rounded-md bg-gray-800 px-4 py-2 text-center text-white hover:bg-gray-700"
        >
          Go to Cart
        </Link>
      </HoverCardContent>
    </HoverCard>
  );
}

function calculateCartTotal(cartItems: Cart["items"]): number {
  return cartItems.reduce((acc, item) => acc + item.product.price * item.quantity, 0);
}
